<template>
  <div class="detail-page">
    <LoadingState v-if="loading" />
    <div v-else-if="error" class="content-width state-block state-error detail-error">
      <strong>{{ error }}</strong>
      <button type="button" class="button button-dark" @click="loadProduct">{{ $store.getters.t('retry') }}</button>
    </div>
    <template v-else-if="product.id">
      <!-- 海报左下说明：只取后台配置的文案，后台没配就不显示（不做任何兜底） -->
      <HeroBanner :banners="detailBanners" :caption="product.heroCaption || product.hero_caption || ''" />
      <section class="product-intro content-width">
        <ProductGallery :product="product" :preferred-image="selectedColorImage" />
        <div class="product-intro__copy">
          <div v-if="modelLogoUrl || productModel" class="product-title-row">
            <img v-if="modelLogoUrl" :src="modelLogoUrl" :alt="productModel ? `${productModel} model logo` : 'Model logo'">
            <span v-if="productModel" class="product-model-name">{{ productModel }}</span>
          </div>
          <p v-if="productDescription" class="product-description">{{ productDescription }}</p>
          <ColorSelector :colors="product.colors" @select="selectColor" />
          <div v-if="product.files && product.files.length" class="download-list">
            <a v-for="file in product.files" :key="file.id" href="#" class="button button-outline" @click.prevent="downloadFile(file)">
              <svg class="download-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M12 3.5v10.2" />
                <path d="M7.8 9.6 12 13.8l4.2-4.2" />
                <path d="M4.6 17.2v1.6a1.7 1.7 0 0 0 1.7 1.7h11.4a1.7 1.7 0 0 0 1.7-1.7v-1.6" />
              </svg>
              <span>{{ $store.getters.t('download') }}</span>
            </a>
          </div>
        </div>
      </section>
      <ProductViews :product="product" />
      <section id="details" class="detail-content">
        <ProductDetails :details="product.details" />
        <div v-if="!product.details || !product.details.length" class="content-width detail-empty"><EmptyState /></div>
        <ProductSpecs :specs="product.specs" />
      </section>
    </template>
    <EmptyState v-else :title="$store.getters.t('noData')" />
    <FloatingHomeButton />
  </div>
</template>

<script>
import { getProduct, listData, recordDownload, recordView } from '../api/portal'
import LoadingState from '../components/LoadingState.vue'
import EmptyState from '../components/EmptyState.vue'
import ProductGallery from '../components/ProductGallery.vue'
import ColorSelector from '../components/ColorSelector.vue'
import ProductDetails from '../components/ProductDetails.vue'
import ProductViews from '../components/ProductViews.vue'
import ProductSpecs from '../components/ProductSpecs.vue'
import HeroBanner from '../components/HeroBanner.vue'
import FloatingHomeButton from '../components/FloatingHomeButton.vue'
import { formatCopyText } from '../utils/copyText'

export default {
  name: 'ProductDetailView',
  components: { LoadingState, EmptyState, ProductGallery, ColorSelector, ProductDetails, ProductViews, ProductSpecs, HeroBanner, FloatingHomeButton },
  data () { return { product: {}, loading: false, error: '', selectedColorImage: '' } },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler () {
        this.loadProduct()
      }
    },
    '$store.getters.language' () {
      this.loadProduct()
    }
  },
  computed: {
    detailBanners () {
      return (this.product.images || []).map((item, index) => ({
        id: item && item.id ? item.id : index,
        imageUrl: typeof item === 'string' ? item : item && (item.imageUrl || item.image_url || item.url),
        mobileImageUrl: typeof item === 'string' ? '' : item && (item.mobileImageUrl || item.mobile_image_url),
        title: ''
      })).filter(item => item.imageUrl)
    },
    productModel () {
      const model = String(this.product.subtitle || '').trim()
      const productName = String(this.product.name || '').trim()
      return model && model.toLocaleLowerCase() !== productName.toLocaleLowerCase() ? model : ''
    },
    productDescription () {
      // 句首大写、其余小写；车型名 / 型号保持大写（不再靠 CSS capitalize）
      return formatCopyText(this.product.description || this.product.summary || '', [
        this.product.name,
        this.product.subtitle,
        this.product.model
      ])
    },
    modelLogoUrl () {
      return this.product.modelLogoUrl || this.product.model_logo_url || ''
    }
  },
  methods: {
    async loadProduct () {
      this.loading = true
      this.error = ''
      this.product = {}
      this.selectedColorImage = ''
      const id = this.$route.params.id
      try {
        const result = await getProduct(id, { lang: this.$store.getters.language })
        const product = (result && (result.product || result)) || {}
        this.product = {
          ...product,
          images: Array.isArray(product && product.images) ? product.images : [],
          colors: Array.isArray(product && product.colors) ? product.colors : [],
          details: Array.isArray(product && product.details) ? product.details : listData(product && product.details),
          specs: Array.isArray(product && product.specs) ? product.specs : listData(product && product.specs),
          files: Array.isArray(product && product.files) ? product.files : listData(product && product.files),
          categories: Array.isArray(product && product.categories) ? product.categories : listData(product && product.categories)
        }
        const firstColorWithImage = this.product.colors.find(color => color && color.imageUrl)
        this.selectedColorImage = (firstColorWithImage && firstColorWithImage.imageUrl) || this.product.coverImage || ''
        if (this.product.id) {
          try {
            await recordView({ contentType: 'product', contentId: this.product.id, contentTitle: this.product.name })
          } catch (analyticsError) {
            // Analytics failure must not block the product page.
          }
        }
      } catch (error) {
        this.error = error.message || this.$store.getters.t('unableLoadProduct')
      } finally {
        this.loading = false
      }
    },
    selectColor (color) { this.selectedColorImage = color && color.imageUrl ? color.imageUrl : (this.product.coverImage || '') },
    async downloadFile (file) {
      try {
        await recordDownload({
          productId: this.product.id,
          fileId: file.id,
          fileName: file.fileName,
          fileUrl: file.fileUrl
        })
      } catch (error) {
        // Download still opens when analytics is unavailable.
      }
      if (file.fileUrl) window.open(file.fileUrl, '_blank', 'noopener')
    }
  }
}
</script>
